import React, { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { oneDark } from '@codemirror/theme-one-dark';
import {
  csharpDarkTheme,
  getLanguageExtension,
  STUDENT_EDITOR_BASIC_SETUP,
  PASTE_BLOCKED_MESSAGE,
  useBlockPasteExtension,
  issueLinesField,
  issueLinesTheme,
  setIssueLines,
  scrollEditorToLine,
  IssueLine,
} from '../../utils/studentEditor';

/**
 * Éditeur étudiant du simulateur : EXACTEMENT la configuration d'ExercisePage
 * (`utils/studentEditor.ts`) — autocomplétion OFF, closeBrackets OFF, collage/drop bloqués,
 * indentation 4 espaces, surlignage des lignes en erreur (console A11).
 */

export interface StudentCodeEditorHandle {
  scrollToLine: (line: number) => void;
}

interface StudentCodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language: string;
  issueLines?: IssueLine[];
  height?: string;
}

export const StudentCodeEditor = forwardRef<StudentCodeEditorHandle, StudentCodeEditorProps>(
  ({ value, onChange, language, issueLines, height = '420px' }, ref) => {
    const editorRef = useRef<ReactCodeMirrorRef>(null);
    const issueLinesRef = useRef(issueLines);
    issueLinesRef.current = issueLines;
    const { blockPasteExtension, pasteBlocked } = useBlockPasteExtension();

    useEffect(() => {
      const view = editorRef.current?.view;
      if (view) view.dispatch({ effects: setIssueLines.of(issueLines ?? []) });
    }, [issueLines]);

    useImperativeHandle(ref, () => ({
      scrollToLine: (line: number) => {
        const view = editorRef.current?.view;
        if (view) scrollEditorToLine(view, line);
      },
    }), []);

    return (
      <div className="relative rounded-lg overflow-hidden code-editor">
        <CodeMirror
          ref={editorRef}
          value={value}
          onChange={onChange}
          onCreateEditor={(view) => {
            if (issueLinesRef.current?.length) {
              view.dispatch({ effects: setIssueLines.of(issueLinesRef.current) });
            }
          }}
          theme={[oneDark, csharpDarkTheme]}
          extensions={[...getLanguageExtension(language), blockPasteExtension, issueLinesField, issueLinesTheme]}
          height={height}
          minHeight="320px"
          placeholder="Écrivez votre code ici..."
          basicSetup={{ ...STUDENT_EDITOR_BASIC_SETUP }}
        />
        <div className="absolute top-2 right-2 bg-gray-700 px-2 py-1 rounded text-xs text-gray-300">
          {language}
        </div>
        {pasteBlocked && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-amber-500 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-lg pointer-events-none">
            {PASTE_BLOCKED_MESSAGE}
          </div>
        )}
      </div>
    );
  }
);

StudentCodeEditor.displayName = 'StudentCodeEditor';

export default StudentCodeEditor;
